/**
 * 挂断原因分类处理
 * 根据 LOVE_ROOM_HANG_UP.md 文档定义的 28 种 end_reason 进行分类和处理
 */

/**
 * 挂断原因枚举（与文档保持一致）
 */
export const END_REASON = {
  // 正常挂断
  ACTIVE_HANG_UP: 'active_hang_up',
  RECEIVER_HANG_UP: 'receiver_hang_up',
  SENDER_HANG_UP: 'sender_hang_up',

  // 超时/拒绝
  TIMEOUT: 'timeout',
  SENDER_TIMEOUT: 'sender_timeout',
  RECEIVER_TIMEOUT: 'receiver_timeout',
  RECEIVER_REFUSE: 'receiver_refuse',
  CANCEL: 'cancel',

  // 网络/RTC问题
  SENDER_RTC_NO_JOIN_CHANNEL: 'sender_rtc_no_join_channel',
  RECEIVER_RTC_NO_JOIN_CHANNEL: 'receiver_rtc_no_join_channel',
  RTC_ERROR: 'rtc_error',
  NETWORK_ERROR: 'network_error',

  // 业务逻辑
  NO_COIN: 'no_coin',
  SENDER_NO_COIN: 'sender_no_coin',
  RECEIVER_NO_COIN: 'receiver_no_coin',
  USER_BUSY: 'user_busy',
  RECEIVER_BUSY: 'receiver_busy',
  SENDER_IN_OTHER_CALL: 'sender_in_other_call',
  RECEIVER_IN_OTHER_CALL: 'receiver_in_other_call',

  // 异常情况
  SERVER_ERROR: 'server_error',
  UNKNOWN: 'unknown',
  KICKED_BY_ADMIN: 'kicked_by_admin',
  TARGET_OFFLINE: 'target_offline',
  TARGET_DELETED: 'target_deleted',
}

/**
 * 挂断原因分类
 */
export const HANGUP_CATEGORY = {
  NORMAL: 'normal',           // 正常挂断
  TIMEOUT: 'timeout',         // 超时/拒绝
  RTC_ISSUE: 'rtc_issue',     // 网络/RTC问题
  BUSINESS: 'business',       // 业务逻辑
  EXCEPTION: 'exception',     // 异常情况
}

/**
 * 判断是否为正常挂断
 */
export function isNormalHangup(endReason) {
  return [
    END_REASON.ACTIVE_HANG_UP,
    END_REASON.RECEIVER_HANG_UP,
    END_REASON.SENDER_HANG_UP,
  ].includes(endReason)
}

/**
 * 判断是否为超时/拒绝
 */
export function isTimeoutOrRefuse(endReason) {
  return [
    END_REASON.TIMEOUT,
    END_REASON.SENDER_TIMEOUT,
    END_REASON.RECEIVER_TIMEOUT,
    END_REASON.RECEIVER_REFUSE,
    END_REASON.CANCEL,
  ].includes(endReason)
}

/**
 * 判断是否为 RTC 问题（需要上传日志）
 */
export function isRTCIssue(endReason) {
  return [
    END_REASON.SENDER_RTC_NO_JOIN_CHANNEL,
    END_REASON.RECEIVER_RTC_NO_JOIN_CHANNEL,
    END_REASON.RTC_ERROR,
    END_REASON.NETWORK_ERROR,
  ].includes(endReason)
}

/**
 * 判断是否为业务逻辑问题
 */
export function isBusinessLogic(endReason) {
  return [
    END_REASON.NO_COIN,
    END_REASON.SENDER_NO_COIN,
    END_REASON.RECEIVER_NO_COIN,
    END_REASON.USER_BUSY,
    END_REASON.RECEIVER_BUSY,
    END_REASON.SENDER_IN_OTHER_CALL,
    END_REASON.RECEIVER_IN_OTHER_CALL,
  ].includes(endReason)
}

/**
 * 判断是否为余额不足
 */
export function isInsufficientBalance(endReason) {
  return [
    END_REASON.NO_COIN,
    END_REASON.SENDER_NO_COIN,
    END_REASON.RECEIVER_NO_COIN,
  ].includes(endReason)
}

/**
 * 判断是否为对方繁忙
 */
export function isUserBusy(endReason) {
  return [
    END_REASON.USER_BUSY,
    END_REASON.RECEIVER_BUSY,
    END_REASON.SENDER_IN_OTHER_CALL,
    END_REASON.RECEIVER_IN_OTHER_CALL,
  ].includes(endReason)
}

/**
 * 判断是否为异常情况
 */
export function isException(endReason) {
  return [
    END_REASON.SERVER_ERROR,
    END_REASON.UNKNOWN,
    END_REASON.KICKED_BY_ADMIN,
    END_REASON.TARGET_OFFLINE,
    END_REASON.TARGET_DELETED,
  ].includes(endReason)
}

/**
 * 判断是否需要上传日志
 * 根据文档第 378-385 行定义
 */
export function shouldUploadLog(endReason) {
  return [
    END_REASON.SENDER_RTC_NO_JOIN_CHANNEL,
    END_REASON.RECEIVER_RTC_NO_JOIN_CHANNEL,
    END_REASON.RTC_ERROR,
    END_REASON.NETWORK_ERROR,
    END_REASON.SERVER_ERROR,
  ].includes(endReason)
}

/**
 * 获取挂断原因分类
 */
export function getHangupCategory(endReason) {
  if (isNormalHangup(endReason)) {
    return HANGUP_CATEGORY.NORMAL
  }
  if (isTimeoutOrRefuse(endReason)) {
    return HANGUP_CATEGORY.TIMEOUT
  }
  if (isRTCIssue(endReason)) {
    return HANGUP_CATEGORY.RTC_ISSUE
  }
  if (isBusinessLogic(endReason)) {
    return HANGUP_CATEGORY.BUSINESS
  }
  if (isException(endReason)) {
    return HANGUP_CATEGORY.EXCEPTION
  }

  // 默认为异常
  return HANGUP_CATEGORY.EXCEPTION
}

/**
 * 根据挂断原因获取处理策略
 * @param {string} endReason - 挂断原因
 * @returns {Object} 处理策略
 */
export function getHangupStrategy(endReason) {
  return {
    category: getHangupCategory(endReason),
    needUploadLog: shouldUploadLog(endReason),
    needShowRecharge: isInsufficientBalance(endReason),
    needShowBusyTips: isUserBusy(endReason),
    isNormal: isNormalHangup(endReason),
  }
}
